import { useEffect } from "react";
import { useSelectionStore } from "../../stores/selectionStore";
import { isUnderFocusDimExemptOverlay } from "../scene/sceneFocusDim";
import { safeTraverse } from "../scene/sceneObjectLookups";

export function useFocusModeDim({
  focusBodyUuid,
  focusCollisionGeomUuid,
  focusMeshUuid,
  focusModeActive,
  getJointIdFromObject,
  getOwningBodyUuidForObject,
  sceneObjects,
  sceneRef,
  selectedBodiesMultiKey,
  selectedBody,
  selectedJoint,
  selectedMesh,
}) {
  // Apply Focus Mode transparency: dim all meshes except the focus body
  useEffect(() => {
    if (!sceneRef.current) return;
    const active = !!focusModeActive;
    const selectedBodiesSnap = useSelectionStore.getState().selectedBodies;
    const multiFocusBodiesUuid =
      active && selectedBodiesSnap.length > 1 ? selectedBodiesSnap : null;

    // Resolve focus body by UUID
    let resolvedFocusBodyUuid = null;

    // If a mesh is selected, resolve its owning body UUID
    if (!resolvedFocusBodyUuid && selectedMesh && sceneRef.current) {
      const meshObj = sceneRef.current.getObjectByName(selectedMesh);
      resolvedFocusBodyUuid = getOwningBodyUuidForObject(meshObj);
    }

    // If a joint is selected, locate its viz group then resolve body UUID
    if (!resolvedFocusBodyUuid && selectedJoint && sceneRef.current) {
      let jointViz = null;
      safeTraverse(sceneRef.current, (obj) => {
        if (
          !jointViz &&
          obj &&
          obj.userData &&
          obj.userData.isJointVizGroup &&
          obj.userData.jointUuid === selectedJoint
        ) {
          jointViz = obj;
          return false;
        }
      });
      resolvedFocusBodyUuid = getOwningBodyUuidForObject(jointViz);
    }

    // Fallbacks by UUID: explicit focusBodyName (stored as UUID) or selectedBody
    if (!resolvedFocusBodyUuid && focusBodyUuid) {
      resolvedFocusBodyUuid = focusBodyUuid;
    }
    if (!resolvedFocusBodyUuid && selectedBody) {
      resolvedFocusBodyUuid = selectedBody;
    }

    safeTraverse(sceneRef.current, (obj) => {
      if (!obj || !obj.isMesh || !obj.material) return;

      // Skip floor/grid
      if (obj.name === "sceneFloor" || obj.name === "sceneGrid") return;

      // Constraint / tendon / equality overlays at scene root — never dim (material color stable).
      if (isUnderFocusDimExemptOverlay(obj)) return;

      // Constraint/tendon marker meshes — do not dim with body focus mode (keeps colors stable).
      if (obj.userData?.isConstraintVisualization) return;

      // Skip joint viz and overlays
      if (
        obj.userData &&
        (getJointIdFromObject(obj) ||
          obj.userData.isJointArc ||
          obj.userData.isGlow ||
          obj.userData.isJointEndpoint)
      )
        return;

      const isDescendantOfMeshToolkitOverlay = (object) => {
        let current = object;
        while (current) {
          if (current.name === "meshToolkitWireframe" || current.name === "faceColors") return true;
          current = current.parent;
        }
        return false;
      };
      if (isDescendantOfMeshToolkitOverlay(obj)) return;

      // Save original material properties once
      if (!obj.userData._focusTouched) {
        if (Array.isArray(obj.material)) {
          obj.userData._origTransparentPerMat = obj.material.map((m) => !!(m && m.transparent));
          obj.userData._origOpacityPerMat = obj.material.map((m) =>
            typeof (m && m.opacity) === "number" ? m.opacity : 1.0
          );
        } else {
          obj.userData._origTransparent = !!obj.material.transparent;
          obj.userData._origOpacity =
            typeof obj.material.opacity === "number" ? obj.material.opacity : 1.0;
        }
        obj.userData._focusTouched = true;
      }

      const applyOpacity = (mat, opacity) => {
        if (!mat) return;
        mat.transparent = true;
        mat.opacity = opacity;
        mat.needsUpdate = true;
      };

      // Determine owning body using UUID-based lookup
      const bodyUuid = getOwningBodyUuidForObject(obj);
      const isInFocusBody = multiFocusBodiesUuid
        ? !!(bodyUuid && multiFocusBodiesUuid.has(bodyUuid))
        : !!resolvedFocusBodyUuid && !!bodyUuid && bodyUuid === resolvedFocusBodyUuid;
      const isFocusMesh = !!focusMeshUuid && obj.userData?.meshName === focusMeshUuid;

      const restoreFocusMaterials = () => {
        if (Array.isArray(obj.material)) {
          const t = obj.userData._origTransparentPerMat || [];
          const o = obj.userData._origOpacityPerMat || [];
          obj.material.forEach((m, i) => {
            if (!m) return;
            m.transparent = !!t[i];
            m.opacity = typeof o[i] === "number" ? o[i] : 1.0;
            m.needsUpdate = true;
          });
        } else {
          obj.material.transparent = !!obj.userData._origTransparent;
          obj.material.opacity =
            typeof obj.userData._origOpacity === "number" ? obj.userData._origOpacity : 1.0;
          obj.material.needsUpdate = true;
        }
      };

      const dimFocusMaterials = () => {
        if (Array.isArray(obj.material)) {
          obj.material.forEach((m) => applyOpacity(m, 0.06));
        } else {
          applyOpacity(obj.material, 0.06);
        }
      };

      if (active && focusCollisionGeomUuid) {
        let owningCollisionGeom = null;
        let cur = obj;
        while (cur) {
          const ud = cur.userData;
          if (ud && ud.type === "collision" && ud.geomUuid) {
            owningCollisionGeom = String(ud.geomUuid);
            break;
          }
          cur = cur.parent;
        }
        const isFocusCollision =
          !!owningCollisionGeom && owningCollisionGeom === String(focusCollisionGeomUuid);
        if (isFocusCollision) {
          restoreFocusMaterials();
        } else {
          dimFocusMaterials();
        }
      } else if (active && (resolvedFocusBodyUuid || multiFocusBodiesUuid)) {
        if (focusMeshUuid) {
          if (isFocusMesh) {
            restoreFocusMaterials();
          } else {
            dimFocusMaterials();
          }
        } else {
          if (isInFocusBody) {
            restoreFocusMaterials();
          } else {
            dimFocusMaterials();
          }
        }
      } else {
        // Restore originals
        if (Array.isArray(obj.material)) {
          const t = obj.userData._origTransparentPerMat || [];
          const o = obj.userData._origOpacityPerMat || [];
          obj.material.forEach((m, i) => {
            if (!m) return;
            m.transparent = !!t[i];
            m.opacity = typeof o[i] === "number" ? o[i] : 1.0;
            m.needsUpdate = true;
          });
        } else {
          obj.material.transparent = !!obj.userData._origTransparent;
          obj.material.opacity =
            typeof obj.userData._origOpacity === "number" ? obj.userData._origOpacity : 1.0;
          obj.material.needsUpdate = true;
        }
        delete obj.userData._focusTouched;
        delete obj.userData._origTransparent;
        delete obj.userData._origOpacity;
        delete obj.userData._origTransparentPerMat;
        delete obj.userData._origOpacityPerMat;
      }
    });
  }, [
    focusModeActive,
    focusBodyUuid,
    focusMeshUuid,
    focusCollisionGeomUuid,
    selectedBody,
    selectedMesh,
    selectedJoint,
    sceneObjects,
    sceneRef,
    getJointIdFromObject,
    getOwningBodyUuidForObject,
    selectedBodiesMultiKey,
  ]);
}
